import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaSpinner,
  FaUserFriends,
} from 'react-icons/fa';
import { userService } from '../../services/apiService';
import './UserProfilePreview.css';

const profileCache = new Map();
const HOVER_DELAY_MS = 320;
const HIDE_DELAY_MS = 180;

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

function formatCount(value) {
  const count = Number(value) || 0;
  if (count >= 1000000) return `${(count / 1000000).toFixed(1).replace('.0', '')}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace('.0', '')}k`;
  return String(count);
}

function computePosition(anchorRect) {
  const cardWidth = 280;
  const cardHeight = 180;
  const gap = 10;
  const viewportPadding = 12;

  let top = anchorRect.bottom + gap;
  let left = anchorRect.left + (anchorRect.width / 2) - (cardWidth / 2);

  if (top + cardHeight > window.innerHeight - viewportPadding) {
    top = anchorRect.top - cardHeight - gap;
  }

  left = Math.max(
    viewportPadding,
    Math.min(left, window.innerWidth - cardWidth - viewportPadding),
  );

  top = Math.max(viewportPadding, top);

  return { top, left, width: cardWidth };
}

export default function UserProfilePreview({ username, children }) {
  const triggerRef = useRef(null);
  const cardRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const requestRef = useRef(0);

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 300 });
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const loadProfile = useCallback(async (targetUsername) => {
    const key = targetUsername.toLowerCase();
    if (profileCache.has(key)) {
      setProfile(profileCache.get(key));
      setLoading(false);
      setError('');
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');

    try {
      const res = await userService.getPublicProfile(targetUsername);
      if (requestRef.current !== requestId) return;
      profileCache.set(key, res.data.profile);
      setProfile(res.data.profile);
    } catch (err) {
      if (requestRef.current !== requestId) return;
      setProfile(null);
      setError(err.message || 'Perfil indisponível.');
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, []);

  const openPreview = useCallback(() => {
    if (!triggerRef.current || !username) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition(computePosition(rect));
    setVisible(true);
    loadProfile(username);
  }, [loadProfile, username]);

  const scheduleShow = useCallback(() => {
    clearTimers();
    showTimerRef.current = window.setTimeout(() => {
      openPreview();
    }, HOVER_DELAY_MS);
  }, [clearTimers, openPreview]);

  const scheduleHide = useCallback(() => {
    clearTimers();
    hideTimerRef.current = window.setTimeout(() => {
      setVisible(false);
    }, HIDE_DELAY_MS);
  }, [clearTimers]);

  const handleEnter = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    scheduleShow();
  };

  const handleLeave = () => {
    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    scheduleHide();
  };

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!visible) return undefined;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      setPosition(computePosition(triggerRef.current.getBoundingClientRect()));
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible]);

  const displayName = profile?.profile?.displayName || username;
  const bio = profile?.profile?.bio?.trim();

  return (
    <>
      <span
        ref={triggerRef}
        className="mention-preview-trigger"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
      >
        {children}
      </span>

      {visible && createPortal(
        <div
          ref={cardRef}
          className="user-profile-preview-card"
          style={{ top: position.top, left: position.left, width: position.width }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          role="tooltip"
        >
          {loading && !profile && (
            <div className="user-profile-preview-loading">
              <FaSpinner className="social-spin" aria-hidden="true" />
              <span>Carregando perfil...</span>
            </div>
          )}

          {!loading && error && (
            <div className="user-profile-preview-error">
              <strong>@{username}</strong>
              <span>{error}</span>
            </div>
          )}

          {profile && (
            <div className="user-profile-preview-body">
              <div className="user-profile-preview-head">
                <span className="user-profile-preview-avatar" aria-hidden="true">
                  {profile.avatarUrl
                    ? <img src={profile.avatarUrl} alt="" />
                    : getInitials(displayName)}
                </span>
                <div className="user-profile-preview-names">
                  <strong>
                    {displayName}
                    {profile.verified && <FaCheckCircle className="post-verified" aria-label="Verificado" />}
                  </strong>
                  <small>@{profile.username}</small>
                </div>
              </div>

              {bio && <p className="user-profile-preview-bio">{bio}</p>}

              <div className="user-profile-preview-stats">
                <span><strong>{formatCount(profile.followersCount)}</strong> seguidores</span>
                <span><strong>{formatCount(profile.followingCount)}</strong> seguindo</span>
                {profile.followsYou && (
                  <span className="user-profile-preview-follows-you">
                    <FaUserFriends aria-hidden="true" /> Segue você
                  </span>
                )}
              </div>

              <Link
                to={`/perfil/${encodeURIComponent(profile.username)}`}
                className="user-profile-preview-cta"
                onClick={(event) => event.stopPropagation()}
              >
                Ver perfil completo
                <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
